import React, { useState, FormEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import raeLogo from '../../assets/images/RAE-LOGO.png';
import loginBg from '../../assets/images/image_login.jpg';
import './LoginForm.css';

export const LoginForm: React.FC = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (!result.success) {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  return (
    <div className="login-split">
      {/* Left panel — background image */}
      <div className="login-image-panel">
        <div className="login-image-placeholder" style={{ backgroundImage: `url(${loginBg})` }}>
          <div className="login-image-overlay-text">
            <span className="login-image-tagline">RAE Robotics</span>
            <span className="login-image-sub">Project Management Platform</span>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="login-form-panel">
        <div className="login-form-inner">
          {/* Logo */}
          <div className="login-logo-wrap">
            <img src={raeLogo} alt="RAE Robotics" className="login-rae-logo" />
          </div>

          <h2 className="login-title">Sign In</h2>
          <p className="login-subtitle">Enter your email and password to access</p>

          <form onSubmit={handleSubmit} className="login-form-fields">
            <div className="lf-group">
              <label htmlFor="email" className="lf-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="user@rae-robotics.com"
                required
                disabled={isLoading}
                autoFocus
                className="lf-input"
              />
            </div>

            <div className="lf-group">
              <label htmlFor="password" className="lf-label">Password</label>
              <div className="lf-password-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                  className="lf-input"
                />
                <button
                  type="button"
                  className="lf-eye-btn"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="lf-error">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="lf-submit"
              disabled={isLoading || !formData.email || !formData.password}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>

          </form>

          <p className="lf-footer">RAE Robotics USA Inc. · Authorized personnel only</p>
        </div>
      </div>
    </div>
  );
};
